import { CenterSpin } from '@/components/CenterSpin';
import ColumnEditor from '@/components/ColumnEditor';
import {
  useCreateDatasetSchemaVersion,
  useDatasetSchemaVersionsByDatasetId,
  useUpdateDatasetSchemaVersion,
} from '@/queries/dataset-schema-versions';
import { useDatasetById } from '@/queries/datasets';
import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined';
import CheckOutlined from '@ant-design/icons/CheckOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import DeleteOutlined from '@ant-design/icons/DeleteOutlined';
import DownOutlined from '@ant-design/icons/DownOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import MenuOutlined from '@ant-design/icons/MenuOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import SaveOutlined from '@ant-design/icons/SaveOutlined';
import {
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import Alert from 'antd/es/alert';
import Badge from 'antd/es/badge';
import Button from 'antd/es/button';
import Dropdown from 'antd/es/dropdown';
import Empty from 'antd/es/empty';
import Form from 'antd/es/form';
import Input from 'antd/es/input';
import InputNumber from 'antd/es/input-number';
import message from 'antd/es/message';
import Select from 'antd/es/select';
import Space from 'antd/es/space';
import Tabs from 'antd/es/tabs';
import Tooltip from 'antd/es/tooltip';
import Typography from 'antd/es/typography';
import { useEffect, useState } from 'react';
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useTheme } from 'styled-components';
import { JsonEditor } from 'ui-components';
import type { Column as DatasetColumn } from '@mai/types';

import {
  LayoutGrid,
  ColumnListPanel,
  ColumnListHeader,
  ColumnListContent,
  ColumnItem,
  DragHandle,
  ColumnInfo,
  ColumnName,
  ColumnSource,
  EditorPanel,
  JsonEditorContainer,
  JsonEditorHeader,
  PageContainer,
  Header,
  ContentArea,
  EmptyState,
} from '@/pages/team/datasets/[datasetId]/schema/styles';
import {
  getTypeBadgeColor,
  createNewColumn,
  prepareColumnsForApi,
  updateColumnPositions,
  formatVersionLabel,
  processParsingOptions,
  getDefaultParsingOptions,
  validateSkipRows,
} from '@/pages/team/datasets/[datasetId]/schema/utils';
import type { ExtendedColumn } from './utils';

const { Text, Title } = Typography;
const { TabPane } = Tabs;

// ---------- Sortable Column Item Component -----------------------------------
interface SortableColumnItemProps {
  column: ExtendedColumn;
  isSelected: boolean;
  onSelect: () => void;
  onDelete: () => void;
  isEditable: boolean;
}

const SortableColumnItem: React.FC<SortableColumnItemProps> = ({
  column,
  isSelected,
  onSelect,
  onDelete,
  isEditable,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: column.key, disabled: !isEditable });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };



  return (
    <div ref={setNodeRef} style={style}>
      <ColumnItem
        $selected={isSelected}
        $isDragging={isDragging}
        onClick={onSelect}
      >
        {isEditable && (
          <DragHandle {...attributes} {...listeners}>
            <MenuOutlined />
          </DragHandle>
        )}

        <ColumnInfo>
          <ColumnName>{column.name}</ColumnName>
          <ColumnSource>{column.source || column.name}</ColumnSource>
        </ColumnInfo>

        <Space>
          <Badge color={getTypeBadgeColor(column.type)} text={column.type} />
          {column.operations && column.operations.length > 0 && (
            <Tooltip title={`${column.operations.length} operations`}>
              <Badge count={column.operations.length} showZero color="blue" />
            </Tooltip>
          )}
          {isEditable && (
            <Button
              type="text"
              size="small"
              icon={<DeleteOutlined />}
              danger
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
            />
          )}
        </Space>
      </ColumnItem>
    </div>
  );
};

// ---------- Main Component ---------------------------------------------------
export const SchemaConfigurationPage: React.FC = () => {
  const { teamId, datasetId } = useParams<{
    teamId: string;
    datasetId: string;
  }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: dataset, isLoading: isLoadingDataset } = useDatasetById(
    datasetId || null,
    { enabled: !!datasetId },
  );

  const { data: schemaVersions, isLoading: isLoadingSchema } =
    useDatasetSchemaVersionsByDatasetId(datasetId || null, {
      enabled: !!datasetId,
    });

  // Get selected version from URL params, fallback to current version
  const selectedVersionId = searchParams.get('versionId');
  const activeSchemaVersion =
    schemaVersions?.items.find((v) =>
      selectedVersionId
        ? v.datasetSchemaVersionId === selectedVersionId
        : v.status === 'current',
    ) || schemaVersions?.items.find((v) => v.status === 'current');

  const createSchemaVersionMutation = useCreateDatasetSchemaVersion();
  const updateSchemaVersionMutation = useUpdateDatasetSchemaVersion();

  const [columns, setColumns] = useState<ExtendedColumn[]>([]);
  const [selectedColumnKey, setSelectedColumnKey] = useState<string | null>(
    null,
  );
  const [activeTab, setActiveTab] = useState('columns');
  const [saving, setSaving] = useState(false);
  const [isEditingJson, setIsEditingJson] = useState(false);
  const [jsonContent, setJsonContent] = useState('');
  const [jsonValid, setJsonValid] = useState(true);
  const [jsonError, setJsonError] = useState<string | undefined>();
  const [parsingOptionsForm] = Form.useForm();

  // Initialize columns from schema version
  useEffect(() => {
    if (activeSchemaVersion) {
      const initialColumns = (activeSchemaVersion.columnsJson || []).map(
        (col: DatasetColumn, idx: number) => ({
          ...col,
          key: `col-${col.pos || idx + 1}`,
          pos: col.pos || idx + 1,
          operations: col.operations || [],
        }),
      );
      setColumns(initialColumns);
    } else if (schemaVersions && schemaVersions.items.length === 0) {
      // No schema versions exist yet, initialize with empty columns
      setColumns([]);
    }
  }, [activeSchemaVersion, schemaVersions]);

  // Initialize parsing options form when activeSchemaVersion changes
  useEffect(() => {
    if (!parsingOptionsForm) return;
    
    try {
      const defaultValues = {
        skipRows: '',
        headerRow: 1,
        delimiter: ',',
        encoding: 'utf-8',
      };
      
      if (activeSchemaVersion?.parsingOptions) {
        const opts = activeSchemaVersion.parsingOptions;
        parsingOptionsForm.setFieldsValue({
          skipRows: Array.isArray(opts.skipRows) 
            ? opts.skipRows.join(',') 
            : (opts.skipRows ? String(opts.skipRows) : ''),
          headerRow: opts.headerRow || 1,
          delimiter: opts.delimiter || ',',
          encoding: opts.encoding || 'utf-8',
        });
      } else {
        parsingOptionsForm.setFieldsValue(defaultValues);
      }
    } catch (error) {
      // Form might not be ready yet, ignore the error
    }
  }, [activeSchemaVersion, parsingOptionsForm]);

  const selectedColumn = columns.find((col) => col.key === selectedColumnKey);
  const isEditable =
    !activeSchemaVersion || activeSchemaVersion.status === 'draft';

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id && over) {
      setColumns((items) => {
        const oldIndex = items.findIndex((item) => item.key === active.id);
        const newIndex = items.findIndex((item) => item.key === over.id);
        const newItems = arrayMove(items, oldIndex, newIndex);

        return updateColumnPositions(newItems);
      });
    }
  };

  const handleAddColumn = () => {
    const newPos = columns.length + 1;
    const newColumn = createNewColumn(newPos);

    setColumns([...columns, newColumn]);
    setSelectedColumnKey(newColumn.key);
  };

  const handleUpdateColumn = (updatedColumn: ExtendedColumn) => {
    setColumns((cols) =>
      cols.map((c) => (c.key === updatedColumn.key ? updatedColumn : c)),
    );
  };

  const handleDeleteColumn = (key: string) => {
    setColumns((cols) => {
      const filtered = cols.filter((c) => c.key !== key);
      return updateColumnPositions(filtered);
    });

    if (selectedColumnKey === key) {
      setSelectedColumnKey(null);
    }
  };

  const handleCreateNewVersion = async () => {
    if (!datasetId || !activeSchemaVersion) {
      void message.error('Unable to create new version');
      return;
    }

    setSaving(true);
    try {
      // Create a new draft version based on the current schema
      const columnsData = prepareColumnsForApi(columns);

      const newVersion = await createSchemaVersionMutation.mutateAsync({
        datasetId,
        columns: columnsData,
        status: 'draft',
        parsingOptions: activeSchemaVersion.parsingOptions,
      });

      // Switch to the new draft version
      setSearchParams({ versionId: newVersion.datasetSchemaVersionId });
      void message.success(
        'New draft version created. You can now edit the schema.',
      );
    } catch (error) {
      void message.error('Failed to create new version');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSchema = async () => {
    if (!datasetId) {
      void message.error('Dataset ID is required');
      return;
    }

    if (columns.length === 0) {
      void message.error('At least one column is required to save the schema');
      return;
    }

    setSaving(true);
    try {
      // Prepare columns for API, removing the temporary key field
      const columnsData = prepareColumnsForApi(columns);

      // Get parsing options from form or JSON content
      let parsingOptions = activeSchemaVersion?.parsingOptions;

      if (jsonContent) {
        // If we have JSON content, use parsing options from JSON
        try {
          const parsed = JSON.parse(jsonContent);
          if (parsed.parsingOptions) {
            parsingOptions = parsed.parsingOptions;
          }
        } catch {
          // If JSON is invalid, fall back to form values
        }
      } else {
        // use form values for parsing options
        const formValues = await parsingOptionsForm.validateFields();
        parsingOptions = processParsingOptions(formValues);
      }

      if (activeSchemaVersion) {
        if (activeSchemaVersion.status === 'draft') {
          // Update existing draft version
          await updateSchemaVersionMutation.mutateAsync({
            datasetSchemaVersionId: activeSchemaVersion.datasetSchemaVersionId,
            data: {
              columns: columnsData,
              parsingOptions,
            },
          });
        } else {
          // Create new version (this should only happen for current/archived versions)
          await createSchemaVersionMutation.mutateAsync({
            datasetId,
            columns: columnsData,
            status: 'current',
            parsingOptions,
          });
        }
      } else {
        // No schema version exists yet, create the first one
        const newVersion = await createSchemaVersionMutation.mutateAsync({
          datasetId,
          columns: columnsData,
          status: 'draft',
          parsingOptions: parsingOptions || getDefaultParsingOptions(),
        });

        // Navigate to the new version
        setSearchParams({ versionId: newVersion.datasetSchemaVersionId });
      }

      void message.success('Schema saved successfully');
    } catch (error) {
      void message.error('Failed to save schema');
    } finally {
      setSaving(false);
    }
  };

  const handlePromoteToCurrent = async () => {
    if (!activeSchemaVersion || activeSchemaVersion.status === 'current') {
      return;
    }

    // before we make it current, we need to ensure that the parsing options are valid
    try {
      await parsingOptionsForm.validateFields();
    } catch (error) {
      void message.error('Please fix validation errors before promoting to current');
      return;
    }

    setSaving(true);
    try {
      await updateSchemaVersionMutation.mutateAsync({
        datasetSchemaVersionId: activeSchemaVersion.datasetSchemaVersionId,
        data: { status: 'current' },
      });
      void message.success('Schema version promoted to current');
    } catch (error) {
      void message.error('Failed to promote schema version');
    } finally {
      setSaving(false);
    }
  };

  const handleArchiveVersion = async () => {
    if (!activeSchemaVersion || activeSchemaVersion.status === 'archived') {
      return;
    }

    setSaving(true);
    try {
      await updateSchemaVersionMutation.mutateAsync({
        datasetSchemaVersionId: activeSchemaVersion.datasetSchemaVersionId,
        data: { status: 'archived' },
      });
      void message.success('Schema version archived');
    } catch (error) {
      void message.error('Failed to archive schema version');
    } finally {
      setSaving(false);
    }
  };

  const handleStartJsonEdit = () => {
    const schemaJson = {
      columnsJson: columns.map(({ key: _key, ...col }) => col),
      parsingOptions: activeSchemaVersion?.parsingOptions || {
        skipRows: [],
        headerRow: 1,
        delimiter: ',',
        encoding: 'utf-8',
      },
    };
    const jsonString = JSON.stringify(schemaJson, null, 2);
    setJsonContent(jsonString);
    setJsonValid(true); // The initial content is always valid
    setJsonError(undefined);
    setIsEditingJson(true);
  };

  const handleCancelJsonEdit = () => {
    setIsEditingJson(false);
    setJsonContent('');
    setJsonError(undefined);
  };

  const handleApplyJsonEdit = () => {
    if (!jsonValid) {
      void message.error('Please fix JSON errors before applying');
      return;
    }

    try {
      const parsed = JSON.parse(jsonContent);
      const newColumns = (parsed.columnsJson || []).map(
        (col: DatasetColumn, idx: number) => ({
          ...col,
          key: `col-${col.pos || idx + 1}`,
          pos: col.pos || idx + 1,
          operations: col.operations || [],
        }),
      );

      setColumns(newColumns);
      // Note: parsingOptions will be included when saving the schema
      setIsEditingJson(false);
      setJsonContent('');
      void message.success('Schema updated from JSON');
    } catch (error) {
      void message.error('Failed to apply JSON changes');
    }
  };

  if (isLoadingDataset || isLoadingSchema) {
    return <CenterSpin />;
  }

  if (!dataset) {
    return (
      <PageContainer>
        <Header>
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate(-1)}
          >
            Back
          </Button>
          <Title level={3}>Dataset not found</Title>
        </Header>
      </PageContainer>
    );
  }

  const renderSchemaStatusBadge = () => {
    if (!activeSchemaVersion) return null;

    const statusColors: Record<string, 'processing' | 'success' | 'default'> = {
      draft: 'processing',
      current: 'success',
      archived: 'default',
    };

    return (
      <Badge
        status={statusColors[activeSchemaVersion.status] || 'default'}
        text={activeSchemaVersion.status.toUpperCase()}
      />
    );
  };

  const renderVersionSelector = () => {
    if (!schemaVersions?.items || schemaVersions.items.length <= 1) return null;



    const menuItems = schemaVersions.items.map((version) => ({
      key: version.datasetSchemaVersionId,
      label: formatVersionLabel(version),
      onClick: () => {
        setSearchParams({ versionId: version.datasetSchemaVersionId });
      },
    }));

    return (
      <Dropdown
        menu={{ items: menuItems }}
        trigger={['click']}
        placement="bottomRight"
      >
        <Button>
          {activeSchemaVersion
            ? formatVersionLabel(activeSchemaVersion)
            : 'Select Version'}
          <DownOutlined style={{ marginLeft: `${theme.marginXS}px` }} />
        </Button>
      </Dropdown>
    );
  };

  return (
    <PageContainer>
      <Header>
        <Link to={`/team/${teamId}/datasets/${datasetId}`}>
          <Button type="text" icon={<ArrowLeftOutlined />}>
            Back
          </Button>
        </Link>
        <Title level={3} style={{ margin: 0 }}>
          Configure Schema – {dataset.name}
        </Title>
        {renderSchemaStatusBadge()}
        {renderVersionSelector()}
        <Space style={{ marginLeft: 'auto' }}>
          <Button onClick={() => navigate(-1)}>Cancel</Button>
          {activeSchemaVersion && activeSchemaVersion.status === 'draft' && (
            <Button
              type="primary"
              onClick={handlePromoteToCurrent}
              loading={saving}
            >
              Make Current
            </Button>
          )}
          {activeSchemaVersion && activeSchemaVersion.status === 'current' && (
            <Button onClick={handleArchiveVersion} loading={saving}>
              Archive
            </Button>
          )}
          {!isEditable && activeSchemaVersion && (
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleCreateNewVersion}
              loading={saving}
            >
              Create New Version
            </Button>
          )}
          {isEditable && (
            <Button
              type="primary"
              icon={<SaveOutlined />}
              onClick={handleSaveSchema}
              loading={saving}
            >
              Save Schema
            </Button>
          )}
        </Space>
      </Header>

      <ContentArea>
        {!activeSchemaVersion &&
          schemaVersions &&
          schemaVersions.items.length === 0 && (
            <Alert
              message="No schema configured"
              description="This dataset doesn't have a schema yet. Start by adding columns to define the structure of your data."
              type="info"
              showIcon
              style={{ marginBottom: `${theme.margin}px` }}
            />
          )}
        {activeSchemaVersion?.status === 'archived' && (
          <Alert
            message="This is an archived schema version. It is read-only and cannot be modified."
            type="info"
            showIcon
            style={{ marginBottom: `${theme.margin}px` }}
          />
        )}

        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane tab="Columns" key="columns">
            <LayoutGrid>
              <ColumnListPanel>
                <ColumnListHeader>
                  <Text strong>Columns ({columns.length})</Text>
                  {isEditable && (
                    <Button
                      type="primary"
                      size="small"
                      icon={<PlusOutlined />}
                      onClick={handleAddColumn}
                    >
                      Add Column
                    </Button>
                  )}
                </ColumnListHeader>
                <ColumnListContent>
                  {columns.length === 0 ? (
                    <Empty
                      description="No columns defined"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={columns.map((col) => col.key)}
                        strategy={verticalListSortingStrategy}
                      >
                        {columns.map((column) => (
                          <SortableColumnItem
                            key={column.key}
                            column={column}
                            isSelected={selectedColumnKey === column.key}
                            onSelect={() => setSelectedColumnKey(column.key)}
                            onDelete={() => handleDeleteColumn(column.key)}
                            isEditable={isEditable}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  )}
                </ColumnListContent>
              </ColumnListPanel>

              <EditorPanel>
                {selectedColumn ? (
                  <ColumnEditor
                    column={selectedColumn}
                    onUpdate={handleUpdateColumn}
                    isEditable={isEditable}
                  />
                ) : (
                  <EmptyState>
                    <Empty
                      description="Select a column to edit its properties"
                      image={Empty.PRESENTED_IMAGE_SIMPLE}
                    />
                  </EmptyState>
                )}
              </EditorPanel>
            </LayoutGrid>
          </TabPane>

          <TabPane tab="Data Preview" key="preview">
            <Alert
              message="Data Preview"
              description="Preview how your data will look after applying the schema configuration. This feature is coming soon."
              type="info"
              showIcon
            />
          </TabPane>

          <TabPane tab="Parsing Options" key="parsing">
            <div
              style={{
                maxWidth: `${theme.controlHeightLG * 12}px`,
                padding: `${theme.paddingLG}px`,
              }}
            >
              <Text
                strong
                style={{
                  fontSize: `${theme.fontSizeLG}px`,
                  display: 'block',
                  marginBottom: `${theme.marginMD}px`,
                }}
              >
                CSV/Excel Parsing Configuration
              </Text>
              <Text
                type="secondary"
                style={{
                  display: 'block',
                  marginBottom: `${theme.marginLG}px`,
                }}
              >
                Configure how your data file should be parsed. These settings
                affect how the raw file data is interpreted.
              </Text>

              <Form
                form={parsingOptionsForm}
                layout="vertical"
                disabled={!isEditable}
              >
                <Form.Item
                  label="Skip Rows"
                  name="skipRows"
                  help="Row indices to skip (0-based). Examples: 0 to skip first row, 1 to skip 2nd row, or comma-separated as '0,1,2' to skip first 3 rows"
                  // we must ensure that the value is a comma-separated string of non-negative integers, and that the integers are valid
                  rules={[
                    {
                      validator: (_, value) => validateSkipRows(value)
                    }
                  ]}
                >
                  <Input
                    style={{ width: '100%' }}
                    placeholder="0,1,2 (leave empty to skip no rows)"
                  />
                </Form.Item>

                <Form.Item
                  label="Header Row"
                  name="headerRow"
                  help="Which row contains the column headers (1-based index)"
                >
                  <InputNumber
                    min={1}
                    max={100}
                    style={{ width: '100%' }}
                    placeholder="1"
                  />
                </Form.Item>

                <Form.Item
                  label="Delimiter"
                  name="delimiter"
                  help="Character used to separate columns in CSV files"
                >
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      { label: 'Comma (,)', value: ',' },
                      { label: 'Semicolon (;)', value: ';' },
                      { label: 'Tab (\\t)', value: '\t' },
                      { label: 'Pipe (|)', value: '|' },
                    ]}
                  />
                </Form.Item>

                <Form.Item
                  label="Encoding"
                  name="encoding"
                  help="Text encoding of the file"
                >
                  <Select
                    style={{ width: '100%' }}
                    options={[
                      { label: 'UTF-8', value: 'utf-8' },
                      { label: 'UTF-16', value: 'utf-16' },
                      { label: 'ISO-8859-1 (Latin-1)', value: 'iso-8859-1' },
                      { label: 'Windows-1252', value: 'windows-1252' },
                    ]}
                  />
                </Form.Item>
              </Form>

              {!isEditable && (
                <Alert
                  message="Read-only mode"
                  description="Create a new version to edit parsing options."
                  type="info"
                  showIcon
                  style={{ marginTop: `${theme.marginMD}px` }}
                />
              )}
            </div>
          </TabPane>

          <TabPane tab="JSON" key="json">
            <JsonEditorContainer>
              {!isEditingJson ? (
                <>
                  <JsonEditorHeader>
                    <Text strong>Schema JSON</Text>
                    {isEditable && (
                      <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={handleStartJsonEdit}
                      >
                        Edit JSON
                      </Button>
                    )}
                  </JsonEditorHeader>
                  <JsonEditor
                    value={JSON.stringify(
                      {
                        columnsJson: columns.map(
                          ({ key: _key, ...col }) => col,
                        ),
                      },
                      null,
                      2,
                    )}
                    height="calc(100% - 40px)"
                    readOnly={true}
                    theme="light"
                  />
                </>
              ) : (
                <>
                  <JsonEditorHeader>
                    <Space>
                      <Text strong>Editing Schema JSON</Text>
                      {!jsonValid && (
                        <Text
                          type="danger"
                          style={{ fontSize: theme.fontSizeSM }}
                        >
                          {jsonError || 'Invalid JSON'}
                        </Text>
                      )}
                    </Space>
                    <Space>
                      <Button
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={handleCancelJsonEdit}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="primary"
                        size="small"
                        icon={<CheckOutlined />}
                        onClick={handleApplyJsonEdit}
                        disabled={!jsonValid}
                      >
                        Apply Changes
                      </Button>
                    </Space>
                  </JsonEditorHeader>
                  <div style={{ flex: 1, overflow: 'auto' }}>
                    <JsonEditor
                      value={jsonContent}
                      onChange={setJsonContent}
                      onValidate={(valid, error) => {
                        setJsonValid(valid);
                        setJsonError(error);
                      }}
                      height="100%"
                      readOnly={false}
                      theme="light"
                    />
                  </div>
                </>
              )}
            </JsonEditorContainer>
          </TabPane>
        </Tabs>
      </ContentArea>
    </PageContainer>
  );
};

export default SchemaConfigurationPage;
